import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "../components/admin/AdminShell";
import { API } from "../lib/eventUtils";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [notify, setNotify] = useState("");
  const [capacity, setCapacity] = useState("24");
  const [partner, setPartner] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/settings`, { withCredentials: true });
        setSettings(data);
        setNotify(data.notify_emails.join("\n"));
        setCapacity(String(data.default_event_capacity));
        setPartner(data.default_partner);
      } catch (e) {
        setError(e.response?.data?.detail || "Could not load settings");
      }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/api/admin/settings`,
        {
          notify_emails: notify.split(/[\n,]/).map((x) => x.trim()).filter(Boolean),
          default_event_capacity: Number(capacity) || 24,
          default_partner: partner,
        },
        { withCredentials: true }
      );
      setSettings(data);
      setNotify(data.notify_emails.join("\n"));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title="Settings">
      <div data-testid="admin-settings-page">
        {error && <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-6" data-testid="settings-error">{error}</p>}
        {!settings && !error && <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-zinc-500 animate-pulse" data-testid="settings-loading">Loading…</p>}
        {settings && (
          <div className="space-y-8 max-w-2xl">
            <div className="border border-white/10 bg-[#0c0c0c] p-6" data-testid="settings-admins-card">
              <p className="eyebrow mb-3">Approved Admins</p>
              <p className="text-zinc-500 text-xs mb-4">
                Sign-in is restricted to these Google accounts. The allowlist is locked in the server environment and cannot be edited here.
              </p>
              <div className="flex flex-wrap gap-2">
                {settings.admin_emails.map((e) => (
                  <span key={e} className="font-mono2 text-[11px] border border-volt/40 text-volt px-3 py-1.5" data-testid={`settings-admin-${e}`}>{e}</span>
                ))}
              </div>
            </div>

            <div className="border border-white/10 bg-[#0c0c0c] p-6" data-testid="settings-notify-card">
              <p className="eyebrow mb-3">Signup Notification Recipients</p>
              <p className="text-zinc-500 text-xs mb-4">
                These addresses get an email on every new event registration. One per line.
                {settings.notify_emails_overridden ? " (Custom list active.)" : " (Currently using the environment default.)"}
              </p>
              <textarea rows={3} value={notify} onChange={(e) => setNotify(e.target.value)} className="volt-input" data-testid="settings-notify-input" />
            </div>

            <div className="border border-white/10 bg-[#0c0c0c] p-6" data-testid="settings-defaults-card">
              <p className="eyebrow mb-4">New Event Defaults</p>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Default capacity</span>
                  <input type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} className="volt-input" data-testid="settings-capacity-input" />
                </label>
                <label className="block">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Default partner</span>
                  <input value={partner} onChange={(e) => setPartner(e.target.value)} className="volt-input" data-testid="settings-partner-input" />
                </label>
              </div>
            </div>

            <div className="border border-white/10 bg-[#0c0c0c] p-6" data-testid="settings-email-card">
              <p className="eyebrow mb-3">Email Sender</p>
              <p className="text-zinc-400 text-sm">
                Emails send as <span className="text-white font-semibold">{settings.email_from_name}</span>. Sender identity and keys live in the server environment — never editable here.
              </p>
            </div>

            <button onClick={save} disabled={busy} className="btn-volt disabled:opacity-60" data-testid="settings-save-button">
              {busy ? "Saving…" : "Save Settings"}
            </button>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
